import Router from '@/Router';
import Store from '@/Store';
import Vue from 'vue';
import * as Config from '@/Config';

/**
 * Module-level flag to prevent duplicate Vue.use() calls
 *
 * @type boolean
 */
let trainerInstalled: boolean = false;

/**
 * Get trainer rendering options for the current device. The Quest browser runs
 * on a mobile-class GPU, so we clamp the device-pixel-ratio and disable
 * antialiasing to keep the headset above its frame budget.
 *
 * @return object
 */
function getRenderingOptions(): object {
	const devicePixelRatio = window.devicePixelRatio || 1;

	if (Config.Environment.IS_QUEST_BROWSER) {
		return {
			maxDprDesktop: Math.max(1, Math.min(1.5, devicePixelRatio)),
			maxDprMobile: Math.max(1, Math.min(1.5, devicePixelRatio)),
			rendererHigh: {
				antialias: false,
			},
			rendererLow: {
				antialias: false,
			},
		};
	}

	return {
		maxDprDesktop: Math.min(1.5, devicePixelRatio * 0.9),
		maxDprMobile: Math.min(1.25, devicePixelRatio * 0.85),
		rendererHigh: {
			antialias: false,
		},
		rendererLow: {
			antialias: false,
		},
		textureExtension: 'avif',
		textureSize: 2048,
	};
}

/**
 * Service for dynamically loading and initializing the ChalkySticks trainer
 * plugin. Handles the async import, Vue.use() registration, and ensures the
 * plugin is only installed once across the application.
 *
 * @author Matt Kenefick <matt@chalkysticks.com>
 * @package Service
 * @project ChalkySticks Quest
 */
const TrainerLoader = {
	/**
	 * Dynamically import and install the ChalkySticks trainer plugin. Safe to
	 * call multiple times; only installs once.
	 *
	 * @return Promise<void>
	 */
	async initialize(): Promise<void> {
		if (trainerInstalled) {
			return;
		}

		const ChalkySticksTrainerModule = await import(
			/* webpackChunkName: "chalkysticks-trainer-runtime" */
			'@chalkysticks/vue2-trainer'
		);

		const ChalkySticksTrainer = (ChalkySticksTrainerModule as any).default ?? (ChalkySticksTrainerModule as any);

		Vue.use(ChalkySticksTrainer, {
			assets: {
				modelUrl: Config.Environment.IS_QUEST_BROWSER ? Config.App.TRAINER_MODEL_QUEST : Config.App.TRAINER_MODEL_DESKTOP,
			},
			environment: {
				default: 'AmsterdamBilliards',
			},
			features: {
				trajectoryPrediction: true,
			},
			provide: {
				router: Router,
				store: Store,
			},
			rendering: getRenderingOptions(),
			sdk: {
				api_url: 'https://api.chalkysticks.com',
			},
			table: {
				appearance: {
					default: 'Traditional',
				},
			},
		});

		trainerInstalled = true;
	},

	/**
	 * Whether the trainer plugin has been installed
	 *
	 * @return boolean
	 */
	get isInstalled(): boolean {
		return trainerInstalled;
	},

	/**
	 * Reset the installation flag, allowing the trainer to be re-initialized.
	 * Useful for retry logic after a failed load.
	 *
	 * @return void
	 */
	reset(): void {
		trainerInstalled = false;
	},
};

export default TrainerLoader;
